import { ReactNode, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { AdminLayout } from "./AdminLayout";
import { SimplifiedWorkspaceLayout } from "./SimplifiedWorkspaceLayout";
import { SubscriptionGateLayout } from "./SubscriptionGateLayout";
import { SimplifiedOnboardingWizard } from "@/components/simplified-onboarding/SimplifiedOnboardingWizard";

interface RoleBasedLayoutProps {
  children: ReactNode;
}

// Stable layout type chosen on first paint, so the chosen layout never
// silently swaps mid-session (which would unmount/remount the entire subtree
// and discard React Query observers + scroll positions).
type LayoutKind =
  | "admin"
  | "subscription-gate"
  | "simplified"
  | null;

export const RoleBasedLayout = ({ children }: RoleBasedLayoutProps) => {
  const { role, loading, isImpersonating, workspace, actualRole, isSuperAdmin, isActuallyPartnerAdmin, isActuallyStaff } = useWorkspace();
  const { canAccessFeatures, isLoading: subscriptionLoading, isExpired } = useSubscriptionStatus();
  const navigate = useNavigate();
  const location = useLocation();

  // Lock the layout choice for the lifetime of this RoleBasedLayout instance
  // (resets on full page refresh). Once we've picked a layout, we keep using
  // it even if the underlying flags briefly flicker during background refetches.
  const lockedLayoutRef = useRef<LayoutKind>(null);

  // Staff outside a workspace use the admin console
  useEffect(() => {
    if (!loading && isActuallyStaff && !isImpersonating) {
      navigate("/admin", { replace: true });
    }
  }, [loading, isActuallyStaff, isImpersonating, navigate]);

  // Redirect to onboarding if no role (user not in any workspace) and not impersonating
  // Super admins (actualRole === 'super_admin') should never redirect to onboarding
  useEffect(() => {
    if (!loading && !role && !isImpersonating && actualRole !== "super_admin" && !isActuallyPartnerAdmin && !isActuallyStaff) {
      navigate("/onboarding");
    }
  }, [role, loading, navigate, isImpersonating, actualRole, isActuallyPartnerAdmin, isActuallyStaff]);


  // Show loading state while determining role or subscription — but ONLY on
  // first paint. Once we've locked a layout, we keep showing it even during
  // background refetches so the subtree never unmounts.
  if ((loading || subscriptionLoading) && lockedLayoutRef.current === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Return null while redirecting (but not for super admins or partner admins)
  if (!role && !isImpersonating && actualRole !== "super_admin" && !isActuallyPartnerAdmin) {
    return null;
  }

  // Determine which layout shell to use. Once chosen for this mount, we keep
  // using the same shell. Inner content/route-specific decisions still
  // re-evaluate per render.
  let layoutKind: LayoutKind = lockedLayoutRef.current;
  if (layoutKind === null) {
    if (role === "super_admin" && !isImpersonating) {
      layoutKind = "admin";
    } else if (!isSuperAdmin && !canAccessFeatures) {
      layoutKind = "subscription-gate";
    } else if (role === "workspace_owner" || role === "workspace_admin" || role === "member") {
      // Every workspace is managed (simplified); the old self-serve layout was removed.
      layoutKind = "simplified";
    }
    if (layoutKind !== null) {
      lockedLayoutRef.current = layoutKind;
    }
  }

  if (layoutKind === "admin") {
    return <AdminLayout>{children}</AdminLayout>;
  }

  if (layoutKind === "subscription-gate") {
    return <SubscriptionGateLayout>{children}</SubscriptionGateLayout>;
  }

  if (layoutKind === "simplified") {
    if (
      location.pathname.startsWith("/inbox") ||
      location.pathname.startsWith("/linkedin-accounts") ||
      location.pathname.startsWith("/knowledge") ||
      location.pathname.startsWith("/company-knowledge") ||
      location.pathname.startsWith("/profiles") ||
      location.pathname.startsWith("/crm") ||
      location.pathname.startsWith("/leads")
    ) {
      return (
        <>
          <SimplifiedWorkspaceLayout>
            {children}
          </SimplifiedWorkspaceLayout>
          
        </>
      );
    }

    const isDetailedCampaignView = true; // Detailed view for every workspace
    const isExcludedCampaignRoute =
      location.pathname === "/campaigns" ||
      location.pathname.startsWith("/campaigns/onboarding") ||
      location.pathname.startsWith("/campaigns/demo") ||
      location.pathname.startsWith("/campaigns/managed") ||
      location.pathname.startsWith("/campaigns/how-it-works") ||
      location.pathname.startsWith("/campaigns/create");

    const isCampaignDetailRoute =
      location.pathname.startsWith("/campaigns/") && !isExcludedCampaignRoute;

    const isCampaignSubRoute =
      location.pathname.endsWith("/settings") ||
      location.pathname.endsWith("/sequence") ||
      location.pathname.endsWith("/audience");

    if (isDetailedCampaignView && isCampaignDetailRoute && isCampaignSubRoute) {
      return (
        <>
          <SimplifiedWorkspaceLayout>
            {children}
          </SimplifiedWorkspaceLayout>
          
        </>
      );
    }

    return (
      <>
        <SimplifiedWorkspaceLayout>
          <SimplifiedOnboardingWizard />
        </SimplifiedWorkspaceLayout>
        
      </>
    );
  }

  navigate("/onboarding");
  return null;
};
